import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown, 
  FileText, 
  Bot, 
  AlertCircle, 
  CheckCircle2, 
  Download,
  RefreshCw,
  Eye,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { SequenceComparisonService, type FileSequenceInfo, type BotInfo, type SequenceComparison } from '@/services/SequenceComparisonService';

interface SequenceVisualizerProps {
  sequence: number[];
  label: string;
  color: 'file' | 'realtime' | 'combined';
  maxDisplay?: number;
}

const SequenceVisualizer: React.FC<SequenceVisualizerProps> = ({ 
  sequence, 
  label, 
  color, 
  maxDisplay = 50 
}) => {
  const [showAll, setShowAll] = useState(false);
  const displaySequence = showAll ? sequence : sequence.slice(0, maxDisplay);
  
  const getColorClass = (color: string) => {
    switch (color) {
      case 'file': return 'bg-blue-900/20 text-blue-300 border-blue-700/50';
      case 'realtime': return 'bg-yellow-900/20 text-yellow-300 border-yellow-700/50';
      case 'combined': return 'bg-purple-900/20 text-purple-300 border-purple-700/50';
      default: return 'bg-gray-900/20 text-gray-300 border-gray-700/50';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">{label}</h4>
        {sequence.length > maxDisplay && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowAll(!showAll)}
            className="h-6 px-2"
          >
            <Eye className="h-3 w-3 mr-1" />
            {showAll ? 'Show Less' : `Show All (${sequence.length})`}
          </Button>
        )}
      </div>
      <div className={`p-2 rounded border-2 ${getColorClass(color)}`}>
        <div className="flex flex-wrap gap-1">
          {displaySequence.map((value, index) => (
            <span
              key={index}
              className={`inline-block w-6 h-6 text-xs font-bold rounded text-center leading-6 ${
                value === 1 
                  ? 'bg-green-500 text-white' 
                  : 'bg-red-500 text-white'
              }`}
            >
              {value}
            </span>
          ))}
          {!showAll && sequence.length > maxDisplay && (
            <span className="inline-block text-xs text-muted-foreground px-2 leading-6">
              ... +{sequence.length - maxDisplay} more
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

interface StatsCardProps {
  title: string;
  total: number;
  wins: number;
  losses: number;
  winRate: number;
  icon: React.ReactNode;
  color: 'file' | 'realtime' | 'combined';
}

const StatsCard: React.FC<StatsCardProps> = ({ 
  title, 
  total, 
  wins, 
  losses, 
  winRate, 
  icon, 
  color 
}) => {
  const getColorClass = (color: string) => {
    switch (color) {
      case 'file': return 'border-blue-700/50 bg-blue-900/10';
      case 'realtime': return 'border-yellow-700/50 bg-yellow-900/10';
      case 'combined': return 'border-purple-700/50 bg-purple-900/10';
      default: return 'border-gray-700/50 bg-gray-900/10';
    }
  };

  return (
    <Card className={`${getColorClass(color)}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="text-2xl font-bold">{total}</div>
        <div className="text-xs space-y-1">
          <div className="flex justify-between">
            <span className="text-green-600">Wins:</span>
            <span className="font-medium">{wins}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-red-600">Losses:</span>
            <span className="font-medium">{losses}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-blue-600">Win Rate:</span>
            <span className="font-bold">{winRate}%</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const SequenceComparisonTab: React.FC = () => {
  const isMobile = useIsMobile();
  const [tradingFiles, setTradingFiles] = useState<FileSequenceInfo[]>([]);
  const [bots, setBots] = useState<BotInfo[]>([]);
  const [selectedFileAId, setSelectedFileAId] = useState<string>('');
  const [selectedFileBId, setSelectedFileBId] = useState<string>('');
  const [selectedBotId, setSelectedBotId] = useState<string>('');
  const [comparison, setComparison] = useState<SequenceComparison | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  // Load available files and bots
  useEffect(() => {
    loadAvailableData();
  }, []);

  const loadAvailableData = async () => {
    setIsLoadingData(true);
    try {
      const [filesResult, botsResult] = await Promise.all([
        SequenceComparisonService.getTradingFiles(),
        SequenceComparisonService.getAvailableBots()
      ]);

      if (filesResult.success && filesResult.data) {
        setTradingFiles(filesResult.data);
      } else {
        toast.error('Failed to load trading files: ' + (filesResult.error || 'Unknown error'));
      }

      if (botsResult.success && botsResult.data) {
        setBots(botsResult.data);
      } else {
        toast.error('Failed to load bots: ' + (botsResult.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleRunComparison = async () => {
    if (!selectedFileAId || !selectedFileBId || !selectedBotId) {
      toast.error('Please select File A, File B, and a Bot');
      return;
    }

    setIsLoading(true);
    try {
      const result = await SequenceComparisonService.compareSequences(selectedFileAId, selectedFileBId, selectedBotId);
      
      if (result.success && result.data) {
        setComparison(result.data);
        toast.success('Sequence comparison completed');
      } else {
        toast.error('Comparison failed: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error running comparison:', error);
      toast.error('Failed to run comparison');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!comparison) return;

    const csvData = SequenceComparisonService.exportToCSV(comparison);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sequence_comparison_${comparison.file_a_info.filename}_vs_${comparison.file_b_info.filename}_${comparison.bot_info.bot_name}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('CSV exported successfully');
  };

  const selectedFileA = tradingFiles.find(f => f.id === selectedFileAId);
  const selectedFileB = tradingFiles.find(f => f.id === selectedFileBId);
  const selectedBot = bots.find(b => b.id === selectedBotId);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Sequence Comparison Tool
          </CardTitle>
          <CardDescription>
            Compare File A sequence with File B + Bot realtime trades to debug pattern inconsistencies
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingData ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin mr-2" />
              Loading files and bots...
            </div>
          ) : (
            <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-3'} gap-4`}>
              {/* File A Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  File A (Baseline)
                </label>
                <Select value={selectedFileAId} onValueChange={setSelectedFileAId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose File A..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tradingFiles.map((file) => (
                      <SelectItem key={file.id} value={file.id}>
                        <div className="flex flex-col">
                          <span>{file.filename}</span>
                          <span className="text-xs text-muted-foreground">
                            {file.total_trades} trades, {file.win_rate}% win rate
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedFileA && (
                  <div className="text-xs text-muted-foreground p-2 bg-blue-900/10 rounded">
                    <strong>File A:</strong> {selectedFileA.total_trades} trades, 
                    {selectedFileA.win_trades} wins, {selectedFileA.loss_trades} losses
                  </div>
                )}
              </div>

              {/* File B Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  File B (Base File)
                </label>
                <Select value={selectedFileBId} onValueChange={setSelectedFileBId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose File B..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tradingFiles.map((file) => (
                      <SelectItem key={file.id} value={file.id}>
                        <div className="flex flex-col">
                          <span>{file.filename}</span>
                          <span className="text-xs text-muted-foreground">
                            {file.total_trades} trades, {file.win_rate}% win rate
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedFileB && (
                  <div className="text-xs text-muted-foreground p-2 bg-green-900/10 rounded">
                    <strong>File B:</strong> {selectedFileB.total_trades} trades, 
                    {selectedFileB.win_trades} wins, {selectedFileB.loss_trades} losses
                  </div>
                )}
              </div>

              {/* Bot Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Bot className="h-4 w-4" />
                  Bot (Realtime Trades)
                </label>
                <Select value={selectedBotId} onValueChange={setSelectedBotId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a bot..." />
                  </SelectTrigger>
                  <SelectContent>
                    {bots.map((bot) => (
                      <SelectItem key={bot.id} value={bot.id}>
                        {bot.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedBot && (
                  <div className="text-xs text-muted-foreground p-2 bg-yellow-900/10 rounded">
                    <strong>Bot:</strong> {selectedBot.name}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button 
              onClick={handleRunComparison}
              disabled={!selectedFileAId || !selectedFileBId || !selectedBotId || isLoading}
              className="flex-1"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  Comparing...
                </>
              ) : (
                <>
                  <Activity className="h-4 w-4 mr-2" />
                  Run Comparison
                </>
              )}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={loadAvailableData}
              disabled={isLoadingData}
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingData ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Comparison Results */}
      {comparison && (
        <div className="space-y-6">
          {/* Validation Status */}
          <Alert className={comparison.is_valid ? "border-green-700/50 bg-green-900/10" : "border-red-700/50 bg-red-900/10"}>
            <div className="flex items-center gap-2">
              {comparison.is_valid ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600" />
              )}
              <AlertDescription>
                {comparison.is_valid ? (
                  "Sequences are valid and ready for analysis"
                ) : (
                  <div>
                    <div className="font-medium">Validation Issues Found:</div>
                    <ul className="list-disc list-inside mt-1">
                      {comparison.validation_errors.map((error, index) => (
                        <li key={index} className="text-sm">{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </AlertDescription>
            </div>
          </Alert>

          {/* Statistics Cards */}
          <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-4'} gap-4`}>
            <StatsCard
              title="File A (Baseline)"
              total={comparison.combined.file_a_stats.total_trades}
              wins={comparison.combined.file_a_stats.win_trades}
              losses={comparison.combined.file_a_stats.loss_trades}
              winRate={comparison.combined.file_a_stats.win_rate}
              icon={<FileText className="h-4 w-4" />}
              color="file"
            />
            
            <StatsCard
              title="File B"
              total={comparison.combined.file_b_stats.total_trades}
              wins={comparison.combined.file_b_stats.win_trades}
              losses={comparison.combined.file_b_stats.loss_trades}
              winRate={comparison.combined.file_b_stats.win_rate}
              icon={<FileText className="h-4 w-4" />}
              color="realtime"
            />
            
            <StatsCard
              title="Bot Trades"
              total={comparison.combined.realtime_stats.total_trades}
              wins={comparison.combined.realtime_stats.win_trades}
              losses={comparison.combined.realtime_stats.loss_trades}
              winRate={comparison.combined.realtime_stats.win_rate}
              icon={<Activity className="h-4 w-4" />}
              color="combined"
            />
            
            <StatsCard
              title="File B + Bot"
              total={comparison.combined.file_b_plus_bot_stats.total_trades}
              wins={comparison.combined.file_b_plus_bot_stats.win_trades}
              losses={comparison.combined.file_b_plus_bot_stats.loss_trades}
              winRate={comparison.combined.file_b_plus_bot_stats.win_rate}
              icon={<TrendingUp className="h-4 w-4" />}
              color="combined"
            />
          </div>

          {/* Sequence Visualizations */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Sequence Visualizations</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleExportCSV}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
              <CardDescription>
                Visual representation of trading sequences (1 = Win, 0 = Loss)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <SequenceVisualizer
                sequence={comparison.combined.file_a_sequence}
                label={`File A (Baseline): ${comparison.file_a_info.filename}`}
                color="file"
              />
              
              <Separator />
              
              <SequenceVisualizer
                sequence={comparison.combined.file_b_sequence}
                label={`File B: ${comparison.file_b_info.filename}`}
                color="realtime"
              />
              
              <Separator />
              
              <SequenceVisualizer
                sequence={comparison.combined.realtime_sequence}
                label={`Bot Realtime Trades: ${comparison.bot_info.bot_name}`}
                color="combined"
              />
              
              <Separator />
              
              <SequenceVisualizer
                sequence={comparison.combined.file_b_plus_bot_sequence}
                label="File B + Bot Combined"
                color="combined"
              />
            </CardContent>
          </Card>

          {/* Difference Analysis */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Phân Tích Điểm Khác Biệt (File A vs File B + Bot)
              </CardTitle>
              <CardDescription>
                So sánh từng giao dịch giữa File A (baseline) và File B + Bot để tìm sự khác biệt
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Summary Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-destructive">
                    {comparison.differenceAnalysis.totalDifferences}
                  </div>
                  <div className="text-sm text-muted-foreground">Giao dịch khác nhau</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-destructive">
                    {comparison.differenceAnalysis.differencePercentage.toFixed(1)}%
                  </div>
                  <div className="text-sm text-muted-foreground">Tỷ lệ khác biệt</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-warning">
                    {comparison.differenceAnalysis.consecutiveDifferences}
                  </div>
                  <div className="text-sm text-muted-foreground">Khác biệt liên tiếp</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold text-primary">
                    {comparison.differenceAnalysis.scatteredDifferences}
                  </div>
                  <div className="text-sm text-muted-foreground">Khác biệt rải rác</div>
                </div>
              </div>

              {/* Impact Assessment */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-lg border">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Đánh Giá Tác Động
                </h4>
                <div className="text-sm space-y-3">
                  {comparison.differenceAnalysis.differencePercentage < 1 ? (
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-green-700 dark:text-green-300 font-medium">Tác động thấp - An toàn sử dụng</p>
                        <p className="text-muted-foreground">Sequences rất tương đồng, có thể sử dụng để train pattern một cách tin cậy</p>
                      </div>
                    </div>
                  ) : comparison.differenceAnalysis.differencePercentage < 5 ? (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="text-yellow-700 dark:text-yellow-300 font-medium">Tác động trung bình - Cần kiểm tra</p>
                        <p className="text-muted-foreground">Có một số khác biệt, nên xem xét kỹ trước khi sử dụng</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
                      <div>
                        <p className="text-red-700 dark:text-red-300 font-medium">Tác động cao - Cần xem xét lại</p>
                        <p className="text-muted-foreground">Nhiều khác biệt có thể ảnh hưởng đến pattern recognition</p>
                      </div>
                    </div>
                  )}
                  {comparison.differenceAnalysis.consecutiveDifferences > comparison.differenceAnalysis.scatteredDifferences && (
                    <div className="flex items-start gap-2 pt-2 border-t">
                      <TrendingUp className="h-4 w-4 text-blue-600 mt-0.5" />
                      <p className="text-blue-700 dark:text-blue-300 text-sm">
                        Khác biệt tập trung trong một giai đoạn - có thể do thay đổi strategy hoặc market conditions
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Visual Overview - Simplified */}
              <div className="space-y-3">
                <h4 className="font-medium">So Sánh Trực Quan</h4>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-blue-600 mb-2">File A (Chuẩn)</div>
                      <div className="h-20 overflow-hidden">
                        <div className="flex flex-wrap gap-0.5">
                          {comparison.combined.file_a_sequence.slice(0, 200).map((value, index) => {
                            const hasDifference = comparison.differenceAnalysis.differences.some(diff => diff.position === index);
                            return (
                              <div
                                key={index}
                                className={`w-2 h-2 rounded-sm ${
                                  hasDifference 
                                    ? 'bg-red-500 ring-1 ring-red-300' 
                                    : value === 1 
                                    ? 'bg-green-500' 
                                    : 'bg-gray-400'
                                }`}
                                title={`Vị trí ${index}: ${value === 1 ? 'Thắng' : 'Thua'}${hasDifference ? ' - Khác biệt!' : ''}`}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="text-sm font-medium text-purple-600 mb-2">File B + Bot</div>
                      <div className="h-20 overflow-hidden">
                        <div className="flex flex-wrap gap-0.5">
                          {comparison.combined.file_b_plus_bot_sequence.slice(0, 200).map((value, index) => {
                            const hasDifference = comparison.differenceAnalysis.differences.some(diff => diff.position === index);
                            return (
                              <div
                                key={index}
                                className={`w-2 h-2 rounded-sm ${
                                  hasDifference 
                                    ? 'bg-red-500 ring-1 ring-red-300' 
                                    : value === 1 
                                    ? 'bg-green-500' 
                                    : 'bg-gray-400'
                                }`}
                                title={`Vị trí ${index}: ${value === 1 ? 'Thắng' : 'Thua'}${hasDifference ? ' - Khác biệt!' : ''}`}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-500 rounded-sm"></div>
                      <span>Thắng</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-sm"></div>
                      <span>Thua</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-red-500 ring-1 ring-red-300 rounded-sm"></div>
                      <span>Điểm khác biệt</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Simplified Differences - Show only important ones */}
              {comparison.differenceAnalysis.differences.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Các Điểm Khác Biệt Quan Trọng</h4>
                    <Badge variant="secondary">
                      {comparison.differenceAnalysis.differences.length} khác biệt
                    </Badge>
                  </div>
                  
                  <div className="space-y-2">
                    {comparison.differenceAnalysis.differences.slice(0, 5).map((diff, index) => (
                      <div key={index} className="p-3 border rounded-lg bg-red-50/50 dark:bg-red-950/20">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-medium">
                            Giao dịch thứ {diff.position + 1}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            File A: {diff.fileAValue === 1 ? 'Thắng' : 'Thua'} → 
                            File B+Bot: {diff.fileBBotValue === 1 ? 'Thắng' : 'Thua'}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Vị trí này có kết quả khác nhau giữa hai sequence
                        </div>
                      </div>
                    ))}
                    
                    {comparison.differenceAnalysis.differences.length > 5 && (
                      <div className="text-center py-2">
                        <Button variant="ghost" size="sm">
                          Xem thêm {comparison.differenceAnalysis.differences.length - 5} khác biệt nữa
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Length Difference */}
              {comparison.differenceAnalysis.lengthDifference > 0 && (
                <Alert className="border-orange-700/50 bg-orange-900/10">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Khác biệt về độ dài:</strong> Sequences có độ dài khác nhau. 
                    File A có {comparison.combined.file_a_sequence.length} giao dịch, 
                    File B + Bot có {comparison.combined.file_b_plus_bot_sequence.length} giao dịch.
                    Chênh lệch: {comparison.differenceAnalysis.lengthDifference} giao dịch.
                  </AlertDescription>
                </Alert>
              )}

              {/* Action Recommendations */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-950/20 dark:to-blue-950/20 p-4 rounded-lg border">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Khuyến Nghị Hành Động
                </h4>
                <div className="text-sm space-y-2">
                  {comparison.differenceAnalysis.differencePercentage < 1 ? (
                    <p className="text-green-700 dark:text-green-300">
                      ✅ <strong>OK để sử dụng:</strong> Sequences rất tương đồng, có thể dùng để train pattern một cách an toàn
                    </p>
                  ) : comparison.differenceAnalysis.differencePercentage < 5 ? (
                    <p className="text-yellow-700 dark:text-yellow-300">
                      ⚠️ <strong>Cần kiểm tra:</strong> Có một số khác biệt, nên điều tra thêm về data source
                    </p>
                  ) : (
                    <p className="text-red-700 dark:text-red-300">
                      🚨 <strong>Cần xem xét lại:</strong> Quá nhiều khác biệt, cần kiểm tra lại data source và xử lý
                    </p>
                  )}
                  
                  <div className="pt-2 border-t text-muted-foreground">
                    <strong>Lưu ý:</strong> Pattern training hoạt động tốt nhất khi sequences có độ tương đồng cao (&lt;2% khác biệt)
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Debug Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Debug Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="space-y-2">
                  <h4 className="font-medium">File A Information:</h4>
                  <div className="bg-blue-900/10 p-3 rounded space-y-1">
                    <div><strong>Filename:</strong> {comparison.file_a_info.filename}</div>
                    <div><strong>File ID:</strong> {comparison.file_a_info.id}</div>
                    <div><strong>Sequence Length:</strong> {comparison.file_a_info.sequence.length}</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-medium">File B Information:</h4>
                  <div className="bg-green-900/10 p-3 rounded space-y-1">
                    <div><strong>Filename:</strong> {comparison.file_b_info.filename}</div>
                    <div><strong>File ID:</strong> {comparison.file_b_info.id}</div>
                    <div><strong>Sequence Length:</strong> {comparison.file_b_info.sequence.length}</div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-medium">Bot Information:</h4>
                  <div className="bg-yellow-900/10 p-3 rounded space-y-1">
                    <div><strong>Bot Name:</strong> {comparison.bot_info.bot_name}</div>
                    <div><strong>Bot ID:</strong> {comparison.bot_info.bot_id}</div>
                    <div><strong>Realtime Trades:</strong> {comparison.bot_info.sequence.length}</div>
                    {comparison.bot_info.oldest_trade_timestamp && (
                      <div><strong>Oldest Trade:</strong> {new Date(comparison.bot_info.oldest_trade_timestamp).toLocaleString()}</div>
                    )}
                    {comparison.bot_info.newest_trade_timestamp && (
                      <div><strong>Newest Trade:</strong> {new Date(comparison.bot_info.newest_trade_timestamp).toLocaleString()}</div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-purple-900/10 p-3 rounded">
                <h4 className="font-medium mb-2">Comparison Analysis:</h4>
                <div className="text-sm space-y-1">
                  <div><strong>File A:</strong> {comparison.combined.file_a_stats.total_trades} trades ({comparison.combined.file_a_stats.win_rate}% win rate)</div>
                  <div><strong>File B:</strong> {comparison.combined.file_b_stats.total_trades} trades ({comparison.combined.file_b_stats.win_rate}% win rate)</div>
                  <div><strong>Bot:</strong> {comparison.combined.realtime_stats.total_trades} realtime trades ({comparison.combined.realtime_stats.win_rate}% win rate)</div>
                  <div><strong>File B + Bot:</strong> {comparison.combined.file_b_plus_bot_stats.total_trades} trades ({comparison.combined.file_b_plus_bot_stats.win_rate}% win rate)</div>
                  <div className="mt-2 pt-2 border-t border-muted-foreground/20">
                    <strong>Comparison:</strong> File A vs File B + Bot sequences for pattern analysis differences
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};